import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import CategoryGrid from "./components/CategoryGrid";
import ComparisonSection from "./components/ComparisonSection";
import SeoContentSection from "./components/SeoContentSection";
import TrustSection from "./components/TrustSection";
import FaqSection from "./components/FaqSection";
import CtaSection from "./components/CtaSection";
import { Link } from "react-router-dom";
import { allArticles, articleCards } from "@/mocks/articles";

const SITE_URL = "https://allchinabuyspreadsheet.biz";

export default function Home() {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <CategoryGrid />
        <ComparisonSection />

        <section className="w-full px-4 md:px-8 py-12 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-600 uppercase tracking-wide">
              <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
              Complete Guide
            </span>
            <Link
              to="/how-to-use-allchinabuy-spreadsheet"
              className="block mt-3 group cursor-pointer"
            >
              <h2 className="text-xl md:text-3xl font-extrabold text-foreground-950 group-hover:text-primary-600 transition-colors">
                How to Use the AllChinaBuy Spreadsheet in 2026
                <br />
                <span className="text-primary-500">(Complete Guide)</span>
              </h2>
            </Link>
            <p className="mt-3 text-base text-foreground-600 max-w-lg mx-auto">
              A step-by-step tutorial covering everything from category navigation to safe buying
              practices — designed for both new and experienced AllChinaBuy users.
            </p>
            <Link
              to="/how-to-use-allchinabuy-spreadsheet"
              className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-md hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Read Full Guide
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 uppercase tracking-wide">
                <i className="ri-newspaper-line w-4 h-4 flex items-center justify-center"></i>
                SEO Guides &amp; Resources
              </span>
              <h2 className="mt-3 text-2xl md:text-4xl font-extrabold text-foreground-950">
                Latest AllChinaBuy Spreadsheet Articles
              </h2>
              <p className="mt-3 text-base text-foreground-600 max-w-xl mx-auto">
                In-depth guides to help you shop smarter, compare sellers, and stay safe on AllChinaBuy
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articleCards.map((card) => {
                const article = allArticles[card.slug];
                if (!article) return null;
                return (
                  <Link
                    key={card.slug}
                    to={`/${card.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 hover:shadow-sm transition-all cursor-pointer flex flex-col"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.imageAlt}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-medium rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                        {article.subtitle}
                      </p>
                      <div className="mt-auto pt-4 flex items-center gap-1 text-xs font-semibold text-primary-600 group-hover:text-primary-700">
                        Read Article
                        <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <SeoContentSection />
        <TrustSection />
        <FaqSection />
        <CtaSection />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "AllChinaBuy Spreadsheet",
              url: SITE_URL,
              potentialAction: {
                "@type": "SearchAction",
                target: `${SITE_URL}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "AllChinaBuy Spreadsheet",
              url: SITE_URL,
              description:
                "The most complete AllChinaBuy Spreadsheet with trusted sellers, product comparisons, and safe buying guides.",
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: `${SITE_URL}/`,
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}